import React, { useEffect, useRef } from 'react';
import { TranscriptionResult, PartialResult, FinalResult } from '../types/transcription';
import './TranscriptionDisplay.css';

interface TranscriptionDisplayProps {
  /** Array of transcription results to display */
  results: TranscriptionResult[];
  /** Whether the transcription session is active */
  isActive: boolean;
  /** Optional className for styling */
  className?: string;
}

/**
 * TranscriptionDisplay component - displays transcription results in Android Live Transcribe style
 * Requirements: 6.1, 6.5 - Display transcription text with large, clear typography and scrolling interface
 */
export const TranscriptionDisplay: React.FC<TranscriptionDisplayProps> = ({
  results,
  isActive,
  className = '',
}) => {
  const scrollContainerRef = useRef<HTMLDivElement>(null);
  const lastResultRef = useRef<HTMLDivElement>(null);
  const previousResultsLength = useRef(0);

  /**
   * Process results to ensure smooth text appending with proper spacing
   * Requirements: 6.4 - Append new segments with appropriate spacing
   */
  const processedResults = React.useMemo(() => {
    return results.map((result, index) => ({
      ...result,
      isNewSegment: index >= previousResultsLength.current,
    }));
  }, [results]);

  // Update previous results length
  React.useEffect(() => {
    previousResultsLength.current = results.length;
  }, [results.length]);

  // Auto-scroll to keep most recent transcription visible - Requirements: 6.4
  useEffect(() => {
    if (lastResultRef.current && scrollContainerRef.current) {
      // Use requestAnimationFrame for smoother scrolling
      requestAnimationFrame(() => {
        lastResultRef.current?.scrollIntoView({
          behavior: 'smooth',
          block: 'end',
          inline: 'nearest',
        });
      });
    }
  }, [results]);

  // Handle smooth appending of new segments
  useEffect(() => {
    if (results.length > 0 && scrollContainerRef.current) {
      const container = scrollContainerRef.current;
      const isScrolledToBottom = 
        container.scrollHeight - container.clientHeight <= container.scrollTop + 50;
      
      // Only auto-scroll if user is already at or near the bottom
      if (isScrolledToBottom) {
        setTimeout(() => {
          container.scrollTo({
            top: container.scrollHeight,
            behavior: 'smooth'
          });
        }, 100);
      }
    }
  }, [results.length]);

  /**
   * Renders a single transcription result segment
   * Requirements: 6.2, 5.3 - Show partial results with lighter text or different formatting
   * Requirements: 6.3, 5.4 - Apply stronger visual emphasis for stable text
   */
  const renderTranscriptionResult = (result: TranscriptionResult, index: number) => {
    const isPartial = result.isPartial;
    const isLast = index === results.length - 1;
    
    // Check if this partial result replaces a previous one
    const partialResult = result as PartialResult;
    const replacesSequence = partialResult.replacesSequence;
    
    return (
      <div
        key={`${result.sequenceNumber}-${result.timestamp}`}
        ref={isLast ? lastResultRef : undefined}
        className={`transcription-segment ${isPartial ? 'partial' : 'final'} ${isLast ? 'latest' : ''}`}
        data-sequence={result.sequenceNumber}
        data-confidence={result.confidence}
        data-replaces={replacesSequence}
      >
        <span className="transcription-text">
          {result.text}
        </span>
        {result.confidence && (
          <span className="confidence-indicator" title={`Confidence: ${Math.round(result.confidence * 100)}%`}>
            {Math.round(result.confidence * 100)}%
          </span>
        )}
      </div>
    );
  };

  return (
    <div className={`transcription-display ${className} ${isActive ? 'active' : 'inactive'}`}>
      <div className="transcription-header">
        <h2>Live Transcription</h2>
        <div className={`status-indicator ${isActive ? 'active' : 'inactive'}`}>
          {isActive ? 'Listening...' : 'Stopped'}
        </div>
      </div>
      
      <div 
        ref={scrollContainerRef}
        className="transcription-scroll-container"
        role="log"
        aria-live="polite"
        aria-label="Live transcription results"
      >
        {results.length === 0 ? (
          <div className="empty-state">
            {isActive ? 'Start speaking to see transcription...' : 'No transcription available'}
          </div>
        ) : (
          <div className="transcription-content">
            {processedResults.map((result, index) => renderTranscriptionResult(result, index))}
          </div>
        )}
      </div>
      
      <div className="transcription-footer">
        <div className="result-count">
          {results.length} segment{results.length !== 1 ? 's' : ''}
        </div>
        {results.length > 0 && (
          <button
            className="scroll-to-bottom-btn"
            onClick={() => {
              if (scrollContainerRef.current) {
                scrollContainerRef.current.scrollTo({
                  top: scrollContainerRef.current.scrollHeight,
                  behavior: 'smooth'
                });
              }
            }}
            title="Scroll to latest transcription"
          >
            ↓ Latest
          </button>
        )}
      </div>
    </div>
  );
};

export type { TranscriptionDisplayProps };